import type {
  ActivityDetail,
  ActivitySummary,
  AuthResponse,
  ExerciseType,
  Gender,
  Page,
  StatsSummary,
  Trackpoint,
  User,
} from './types'
import { TERMS_VERSION } from '../legal'

const TOKEN_KEY = 'mytracker.token'

/**
 * Same-origin by default: the dev server proxies /api to the backend and in production the site is
 * served from the same host, so the token never crosses an origin boundary.
 */
const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorised(): boolean {
    return this.status === 401 || this.status === 403
  }
}

export function storedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Private browsing can refuse storage entirely; the session then lasts this page load only.
    return null
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token === null) {
      localStorage.removeItem(TOKEN_KEY)
    } else {
      localStorage.setItem(TOKEN_KEY, token)
    }
  } catch {
    /* Nothing to do: the app works without persistence, it just forgets on reload. */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = storedToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch {
    throw new ApiError(0, 'Could not reach the My Tracker server.')
  }

  if (!response.ok) {
    throw new ApiError(response.status, await errorMessage(response))
  }
  // A body is optional on success: 204 from logout and delete, 202 from the reset request. Asking
  // fetch for JSON on an empty body throws, so the body is read as text and parsed only if there
  // is one — which keeps a successful call from surfacing as a parse error.
  const text = await response.text()
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

/** Prefers the ProblemDetail "detail" the API sends, and falls back to the status text. */
async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: string; message?: string }
    return body.detail ?? body.message ?? `Request failed (${response.status})`
  } catch {
    return response.status === 401
      ? 'Your session has expired. Sign in again.'
      : `Request failed (${response.status})`
  }
}

export const api = {
  login(email: string, password: string): Promise<AuthResponse> {
    return request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  /** The server refuses a sign-up that has not accepted the disclaimer and privacy policy. */
  register(email: string, password: string, displayName: string): Promise<AuthResponse> {
    return request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        displayName,
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
      }),
    })
  },

  /**
   * Asks for a reset link. Resolves the same way for an unregistered address as for a registered
   * one — the API answers identically on purpose, so the caller must not infer anything from it.
   */
  forgotPassword(email: string): Promise<void> {
    return request<void>('/api/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  /** Spends the token from the emailed link. Signs the account out everywhere on success. */
  resetPassword(token: string, password: string): Promise<void> {
    return request<void>('/api/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    })
  },

  me(): Promise<User> {
    return request<User>('/api/me')
  },

  /**
   * Writes the profile. A field sent as null is cleared; a field left out of the object is left
   * as it is, which is how the Android app can save a weight without disturbing anything else.
   */
  updateProfile(profile: {
    displayName?: string
    heightCm?: number | null
    weightKg?: number | null
    dateOfBirth?: string | null
    gender?: Gender | null
    country?: string | null
  }): Promise<User> {
    return request<User>('/api/me', { method: 'PATCH', body: JSON.stringify(profile) })
  },

  /**
   * Deletes the account and every activity on it. Irreversible, and the token dies with it.
   * The current password is required even though the call is already authenticated.
   */
  deleteAccount(password: string): Promise<void> {
    return request<void>('/api/me', { method: 'DELETE', body: JSON.stringify({ password }) })
  },

  /** Revokes every token issued to this account, including the one making this call. */
  logout(): Promise<void> {
    return request<void>('/api/auth/logout', { method: 'POST' })
  },

  activities(page = 0, size = 25): Promise<Page<ActivitySummary>> {
    return request<Page<ActivitySummary>>(`/api/activities?page=${page}&size=${size}`)
  },

  activity(id: string): Promise<ActivityDetail> {
    return request<ActivityDetail>(`/api/activities/${id}`)
  },

  track(id: string): Promise<Trackpoint[]> {
    return request<Trackpoint[]>(`/api/activities/${id}/track`)
  },

  stats(days: number): Promise<StatsSummary> {
    return request<StatsSummary>(`/api/stats/summary?days=${days}`)
  },

  updateNotes(id: string, notes: string): Promise<ActivitySummary> {
    return request<ActivitySummary>(`/api/activities/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    })
  },

  /**
   * Reclassifies a run. Only the stored record changes: the original TCX is kept exactly as it
   * was uploaded, so a download afterwards still carries whatever the phone wrote.
   */
  updateExerciseType(id: string, exerciseType: ExerciseType): Promise<ActivitySummary> {
    return request<ActivitySummary>(`/api/activities/${id}/exercise-type`, {
      method: 'PATCH',
      body: JSON.stringify({ exerciseType }),
    })
  },

  deleteActivity(id: string): Promise<void> {
    return request<void>(`/api/activities/${id}`, { method: 'DELETE' })
  },

  /** Uploads a .tcx picked in the browser, for runs that came off a watch rather than the app. */
  uploadTcx(xml: string): Promise<Array<{ activityId: string; created: boolean }>> {
    return request<Array<{ activityId: string; created: boolean }>>('/api/activities/tcx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/xml' },
      body: xml,
    })
  },

  /** Downloads the stored file unchanged, so it can be taken to Garmin Connect or Strava. */
  async downloadTcx(id: string, filename: string): Promise<void> {
    const token = storedToken()
    const response = await fetch(`${BASE}/api/activities/${id}/tcx`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    if (!response.ok) {
      throw new ApiError(response.status, await errorMessage(response))
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  },
}
